import { FC, useCallback, useMemo } from 'react';

import { date, dom, kind } from 'app/utils';
import { Calendar as Component, CalendarProps as Props } from '../external';
import { IEvent, IInput } from './types';

export interface IDateBox extends IInput<string | Date | null | (string | Date | null)[]> {
  min?: Date;
  max?: Date;

  buttons?: boolean;
  selection?: 'single' | 'range' | 'multiple';
  inline?: boolean;

  hourFormat?: '12' | '24';
  timeFormat?: '00:00' | '00:00:00' | '00:00:00.000';
  timeOnly?: boolean;
  time?: boolean;
  utc?: boolean;
  seconds?: boolean;
  milliseconds?: boolean;
  week?: boolean;

  icon?: string;
  nextIcon?: string;
  prevIcon?: string;
}

export const DateBox: FC<IDateBox> = ({ id, className, style, disabled, name, value, placeholder, tooltip, autoFocus, readOnly, invalid, min, max, buttons, selection, inline, hourFormat, timeOnly, time, utc, seconds, milliseconds, week, onBlur, onChange, onFocus }) => {
  if (!Array.isArray(value)) value = (value ? [value] : []);

  const values = value.reduce<Date[]>((current, value) => {
    const buffer = (utc ? date.local(value) : date.from(value));
    if (buffer) current.push(buffer);

    return current;
  }, []);

  const processors = useMemo(() => {
    const stack = [];
    if (utc) stack.push(date.utc);
    if (!time) stack.push(date.trim);

    return stack;
  }, [time, utc]);

  const process = useCallback((value?: string | Date | null) => {
    return processors.reduce((o, p) => p(o), value);
  }, [processors]);

  const prepare = useCallback((value?: string | Date | null | (string | Date | null)[]) => ({
    target: { id, name,
      value: (kind.isArray(value) ? value.map(process) : process(value)),
    },
  } as IEvent<Date>), [id, name, process]);

  const selectionMode = (selection ?? 'single');

  const props: Props = { id, style, disabled, name, placeholder, tooltip, autoFocus, invalid, selectionMode, inline, hourFormat, timeOnly, onBlur, onFocus,
    className: dom.className('i-component', 'i-input', 'i-datebox', { 'i-invalid': invalid }, className),

    value: (['multiple', 'range'].includes(selectionMode) ? values : values[0]) as any,

    readOnlyInput: readOnly,
    minDate: min,
    maxDate: max,

    showButtonBar: (buttons ?? true),
    showTime: time,
    showSeconds: seconds,
    showMillisec: milliseconds,
    showWeek: week,

    onChange: dom.handle(onChange, ({ value }) => prepare(value)),
    onHide: dom.handle(onBlur, () => prepare(value)),
  };

  return (
    <Component { ...props } />
  );
}
